/**
 * Tiny admin SPA — login, customer search, customer detail (resync/override),
 * coupons CRUD, failed jobs, webhook events.
 *
 * Auth: cookie set by POST /api/admin/login.
 */
import React, { useEffect, useState } from "react";
import { Route, Switch, useLocation, useRoute } from "wouter";
import { api, ApiError, formatBRL } from "../../lib/api.js";
import { BONUS_FEATURE_KEYS } from "../../../../shared/order_bumps.js";

interface Me {
  admin: { email: string };
}

export function AdminApp() {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api<Me>("/api/admin/me")
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8">Loading…</div>;
  if (!me) return <Login onLogin={(m) => setMe(m)} />;
  return (
    <div className="min-h-screen">
      <Topbar email={me.admin.email} onLogout={() => setMe(null)} />
      <div className="p-6 max-w-6xl mx-auto">
        <Switch>
          <Route path="/" component={Customers} />
          <Route path="/customers" component={Customers} />
          <Route path="/customers/:id" component={CustomerDetail} />
          <Route path="/audit" component={AuditLog} />
          <Route path="/coupons" component={Coupons} />
          <Route path="/order-bumps" component={OrderBumps} />
          <Route path="/jobs" component={JobsView} />
          <Route path="/webhooks" component={Webhooks} />
        </Switch>
      </div>
    </div>
  );
}

function Topbar({ email, onLogout }: { email: string; onLogout: () => void }) {
  const [, setLocation] = useLocation();
  return (
    <div className="flex items-center justify-between p-4 border-b bg-white">
      <div className="font-semibold">zenpay admin</div>
      <nav className="flex gap-4 text-sm">
        <a href="/admin/customers">Customers</a>
        <a href="/admin/audit">Audit</a>
        <a href="/admin/coupons">Coupons</a>
        <a href="/admin/order-bumps">Order bumps</a>
        <a href="/admin/jobs">Jobs</a>
        <a href="/admin/webhooks">Webhooks</a>
      </nav>
      <div className="text-sm flex items-center gap-4">
        <span className="text-ink-2">{email}</span>
        <button
          className="underline"
          onClick={async () => {
            await api("/api/admin/logout", { method: "POST" });
            onLogout();
            setLocation("/");
          }}
        >
          Logout
        </button>
      </div>
    </div>
  );
}

function Login({ onLogin }: { onLogin: (me: Me) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    try {
      await api("/api/admin/login", { method: "POST", body: JSON.stringify({ email, password }) });
      const me = await api<Me>("/api/admin/me");
      onLogin(me);
    } catch (e) {
      if (e instanceof ApiError) setErr("Credenciais inválidas.");
      else setErr("Erro.");
    }
  }
  return (
    <main className="min-h-screen flex items-center justify-center bg-[#f7f5fc] p-4">
      <form onSubmit={submit} className="card-glass max-w-sm w-full p-6 space-y-3">
        <h1 className="text-lg font-semibold">Admin</h1>
        <input className="input" placeholder="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <input className="input" type="password" placeholder="senha" value={password} onChange={(e) => setPassword(e.target.value)} />
        {err && <div className="text-danger text-sm">{err}</div>}
        <button className="btn-primary w-full">Entrar</button>
      </form>
    </main>
  );
}

function Customers() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState<Array<{ id: string; email: string; phone: string; cpf: string; createdAt: string }>>([]);
  useEffect(() => {
    api<{ customers: typeof items }>(`/api/admin/customers?q=${encodeURIComponent(q)}`).then((r) =>
      setItems(r.customers),
    );
  }, [q]);
  return (
    <div>
      <input className="input mb-4" placeholder="Buscar por email, telefone, CPF, provider id…" value={q} onChange={(e) => setQ(e.target.value)} />
      <table className="w-full text-sm">
        <thead className="text-left text-ink-2">
          <tr>
            <th>Email</th>
            <th>Phone</th>
            <th>CPF</th>
            <th>Criado em</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} className="border-t">
              <td className="py-2">{c.email}</td>
              <td>{c.phone}</td>
              <td>{c.cpf}</td>
              <td>{new Date(c.createdAt).toLocaleDateString()}</td>
              <td>
                <a className="underline" href={`/admin/customers/${c.id}`}>Detalhes</a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A card change approved in place on Stripe is undone here, not by canceling the row: Stripe
 * already charges the new price from the effective date, and the server puts the current one back.
 */
function ScheduledChangesSection({ rows, onChanged }: { rows: any[]; onChanged: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function revert(row: any) {
    const reason = window.prompt("Motivo (fica na auditoria):");
    if (!reason || reason.trim().length < 3) return;
    const when = new Date(row.effectiveAt).toLocaleDateString();
    // The Stripe price before the change, in its own currency (a coupon or offer price, not the list).
    const price = row.revert_price as { unitAmount: number; currency: string } | null;
    const amount = price
      ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: price.currency.toUpperCase() }).format(price.unitAmount / 100)
      : "o preço de antes";
    if (!window.confirm(`Desfazer a mudança de ${when}? O Stripe volta a cobrar ${amount} a partir dessa data e o plano atual fica.`)) {
      return;
    }
    setError(null);
    setBusyId(row.id);
    try {
      await api(`/api/admin/scheduled-changes/${row.id}/revert-in-place`, {
        method: "POST",
        body: JSON.stringify({ reason: reason.trim() }),
      });
      onChanged();
    } catch (err) {
      const code = err instanceof ApiError ? ((err.body as { error?: string } | null)?.error ?? err.status) : String(err);
      setError(`Não foi possível desfazer: ${code}`);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section>
      <h2 className="font-semibold">Scheduled changes</h2>
      {error && <div className="text-sm text-red-600 mt-1">{error}</div>}
      <ul className="text-sm border rounded divide-y mt-2">
        {rows.length === 0 && <li className="p-2 text-ink-2">Nenhuma</li>}
        {rows.map((row) => (
          <li key={row.id} className="p-2 flex items-center justify-between gap-3">
            <span>
              {new Date(row.effectiveAt).toLocaleDateString()} · {row.kind} ·{" "}
              {row.payload?.target_plan}/{row.payload?.target_interval}/{row.payload?.target_payment_method} ·{" "}
              {row.appliedAt ? "aplicada" : row.canceledAt ? "cancelada" : (row.providerSwapStatus ?? "agendada")}
            </span>
            {row.revertible_in_place && (
              <button className="btn-ghost" disabled={busyId === row.id} onClick={() => revert(row)}>
                Desfazer (Stripe)
              </button>
            )}
          </li>
        ))}
      </ul>
      <details className="mt-2">
        <summary className="text-xs text-ink-2 cursor-pointer">JSON</summary>
        <pre className="text-xs bg-black/5 rounded p-3 overflow-auto">{JSON.stringify(rows, null, 2)}</pre>
      </details>
    </section>
  );
}

function supportAccessError(err: unknown): string {
  if (!(err instanceof ApiError)) return String(err);
  const code = (err.body as { error?: unknown } | null)?.error;
  if (code === "cancellation_scheduled") return "Há um cancelamento agendado: desfaça o cancelamento antes de liberar.";
  if (typeof code === "string") return `Não foi possível: ${code}`;
  return `Não foi possível (${err.status}). Confira os dias (1 a 30) e o motivo.`;
}

/**
 * Access granted by support until a date (PIT-468). The fix support used to make in Zenpost alone was
 * undone by the next morning's sync; a grant here is what the syncs respect.
 */
function SupportAccessSection({
  customerId,
  customer,
  onChanged,
}: {
  customerId: string;
  customer: { supportAccessUntil?: string | null; supportAccessNote?: string | null };
  onChanged: () => void;
}) {
  const [days, setDays] = useState(7);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const until = customer.supportAccessUntil ? new Date(customer.supportAccessUntil) : null;
  const running = !!until && until.getTime() > Date.now();

  async function grant() {
    setBusy(true);
    setError(null);
    try {
      await api(`/api/admin/customers/${customerId}/support-access`, {
        method: "POST",
        body: JSON.stringify({ days, note }),
      });
      setNote("");
      onChanged();
    } catch (err) {
      setError(supportAccessError(err));
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    setBusy(true);
    setError(null);
    try {
      await api(`/api/admin/customers/${customerId}/support-access`, { method: "DELETE" });
      onChanged();
    } catch (err) {
      setError(supportAccessError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card-glass p-4 space-y-2">
      <h2 className="font-semibold">Acesso liberado pelo suporte</h2>
      <p className="text-sm text-ink-2">
        {running
          ? `Liberado até ${until!.toLocaleDateString()}: ${customer.supportAccessNote ?? ""}`
          : "Nenhum. Liberar aqui, e não só no Zenpost, é o que impede o sync do dia seguinte de desfazer."}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-sm">
          Dias{" "}
          <input
            type="number"
            min={1}
            max={30}
            value={days}
            onChange={(e) => setDays(Math.min(30, Math.max(1, Math.round(Number(e.target.value) || 1))))}
            className="input w-20"
          />
        </label>
        <input
          placeholder="Motivo (ex.: pagou o PIX, renovação não chegou)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="input flex-1 min-w-48"
        />
        <button className="btn-ghost" disabled={busy || note.trim().length < 3} onClick={grant}>
          Liberar acesso
        </button>
        {running && (
          <button className="btn-ghost" disabled={busy} onClick={revoke}>
            Encerrar
          </button>
        )}
      </div>
      {error && <div className="text-sm text-red-600">{error}</div>}
    </section>
  );
}

function CustomerDetail() {
  const [, params] = useRoute<{ id: string }>("/customers/:id");
  const id = params?.id;
  const [data, setData] = useState<any>(null);
  function refresh() {
    if (!id) return;
    api(`/api/admin/customers/${id}`).then(setData);
  }
  useEffect(refresh, [id]);

  async function resync() {
    if (!id) return;
    await api(`/api/admin/customers/${id}/resync`, { method: "POST" });
    refresh();
  }

  if (!data) return <div>Loading…</div>;
  return (
    <div className="space-y-6">
      <section className="card-glass p-4">
        <div className="font-semibold">{data.customer.email}</div>
        <div className="text-sm text-ink-2">{data.customer.phone} · {data.customer.cpf}</div>
        <div className="flex gap-2 mt-3">
          <button className="btn-ghost" onClick={resync}>Re-sync</button>
        </div>
      </section>

      <SupportAccessSection customerId={id!} customer={data.customer} onChanged={refresh} />

      <IntegratedLinkSection customerId={id!} email={data.customer.email} />

      <section>
        <h2 className="font-semibold">Subscription</h2>
        <pre className="text-xs bg-black/5 rounded p-3 overflow-auto">{JSON.stringify(data.subscription, null, 2)}</pre>
      </section>

      <ScheduledChangesSection rows={data.scheduled_changes} onChanged={refresh} />

      <section>
        <h2 className="font-semibold">Payments</h2>
        <ul className="text-sm border rounded divide-y">
          {data.payments.map((p: any) => (
            <li key={p.id} className="p-2 flex justify-between">
              <span>{new Date(p.paidAt).toLocaleDateString()} · {p.description}</span>
              <span>{formatBRL(p.amountCents)} · {p.status}</span>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className="font-semibold">Orders</h2>
        <ul className="text-sm border rounded divide-y">
          {data.orders.map((o: any) => (
            <li key={o.id} className="p-2 flex justify-between">
              <span>{new Date(o.createdAt).toLocaleDateString()} · {o.plan}/{o.interval}/{o.paymentMethod}</span>
              <span>{formatBRL(o.finalCents)} · {o.status}</span>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className="font-semibold mb-2">Audit (cliente)</h2>
        <AuditTable rows={data.audit} showCustomerLink={false} />
      </section>
    </div>
  );
}

interface AuditRow {
  id: number;
  customerId: string | null;
  customerEmail?: string | null;
  subscriptionId: string | null;
  orderId: string | null;
  actor: string;
  action: string;
  detail: unknown;
  createdAt: string;
}

function AuditTable({
  rows,
  showCustomerLink = true,
}: {
  rows: AuditRow[];
  showCustomerLink?: boolean;
}) {
  if (!rows.length) {
    return <p className="text-sm text-ink-2">Nenhum registro.</p>;
  }

  return (
    <div className="border rounded overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-ink-2 bg-black/[0.03]">
          <tr>
            <th className="p-2 whitespace-nowrap">Quando</th>
            {showCustomerLink ? <th className="p-2">Cliente</th> : null}
            <th className="p-2">Actor</th>
            <th className="p-2">Action</th>
            <th className="p-2">Detail</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-t align-top">
              <td className="p-2 whitespace-nowrap text-xs">
                {new Date(row.createdAt).toLocaleString()}
              </td>
              {showCustomerLink ? (
                <td className="p-2">
                  {row.customerId ? (
                    <a className="underline" href={`/admin/customers/${row.customerId}`}>
                      {row.customerEmail ?? row.customerId.slice(0, 8)}
                    </a>
                  ) : (
                    <span className="text-ink-2">—</span>
                  )}
                </td>
              ) : null}
              <td className="p-2 text-xs">{row.actor}</td>
              <td className="p-2 font-mono text-xs">{row.action}</td>
              <td className="p-2 text-xs text-ink-2 max-w-md">
                {row.detail ? (
                  <details>
                    <summary className="cursor-pointer">JSON</summary>
                    <pre className="mt-1 bg-black/5 rounded p-2 overflow-auto max-h-40">
                      {JSON.stringify(row.detail, null, 2)}
                    </pre>
                  </details>
                ) : (
                  "—"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditLog() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);

  function refresh() {
    setLoading(true);
    const params = new URLSearchParams({ limit: "300" });
    if (q.trim()) params.set("q", q.trim());
    api<{ audit: AuditRow[] }>(`/api/admin/audit?${params.toString()}`)
      .then((r) => setRows(r.audit))
      .finally(() => setLoading(false));
  }

  useEffect(refresh, []);

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Audit log</h1>
      <div className="flex gap-2">
        <input
          className="input flex-1"
          placeholder="Buscar por email, action, actor, customer id…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") refresh();
          }}
        />
        <button type="button" className="btn-primary" onClick={refresh}>
          Buscar
        </button>
      </div>
      {loading ? <div>Loading…</div> : <AuditTable rows={rows} />}
    </div>
  );
}

function IntegratedLinkSection({ customerId, email }: { customerId: string; email: string }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<{ url: string; token: string } | null>(null);
  const [copied, setCopied] = useState(false);

  async function generate() {
    setLoading(true);
    setError(null);
    setCopied(false);
    try {
      const r = await api<{ url: string; token: string }>(
        `/api/admin/customers/${customerId}/integrated-url`,
        { method: "POST" },
      );
      setLink(r);
    } catch {
      setError("Não foi possível gerar o link.");
    } finally {
      setLoading(false);
    }
  }

  async function copyUrl() {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Não foi possível copiar.");
    }
  }

  return (
    <section className="card-glass p-4 space-y-3">
      <div>
        <h2 className="font-semibold">Link /integrated</h2>
        <p className="text-sm text-ink-2 mt-1">
          Abre a experiência de billing como <span className="font-medium">{email}</span> (suporte / debug).
          Trate o link como credencial sensível.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-primary" onClick={() => void generate()} disabled={loading}>
          {loading ? "Gerando…" : link ? "Regenerar link" : "Gerar link"}
        </button>
        {link ? (
          <>
            <button type="button" className="btn-ghost" onClick={() => void copyUrl()}>
              {copied ? "Copiado!" : "Copiar URL"}
            </button>
            <a className="btn-ghost" href={link.url} target="_blank" rel="noopener noreferrer">
              Abrir em nova aba
            </a>
          </>
        ) : null}
      </div>
      {error ? <p className="text-sm text-danger">{error}</p> : null}
      {link ? (
        <div className="space-y-2">
          <input className="input text-xs font-mono" readOnly value={link.url} onFocus={(e) => e.target.select()} />
          <details>
            <summary className="text-xs text-ink-2 cursor-pointer">Token JWT</summary>
            <pre className="text-xs bg-black/5 rounded p-2 mt-1 overflow-auto max-h-32 break-all">{link.token}</pre>
          </details>
        </div>
      ) : null}
    </section>
  );
}

const EMPTY_BUMP_FORM = {
  key: "",
  internal_name: "",
  headline: "",
  description: "",
  checkbox_label: "",
  price_monthly_cents: 500,
  /** Empty: the checkout shows the computed "de" price (30% above). */
  compare_at_monthly_cents: "" as number | "",
  image_url: "",
  video_url: "",
  sort_order: 0,
};

function OrderBumps() {
  const [items, setItems] = useState<any[]>([]);
  const [form, setForm] = useState(EMPTY_BUMP_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const usedKeys = new Set(items.map((b) => b.key));
  const freeKeys = BONUS_FEATURE_KEYS.filter((k) => !usedKeys.has(k));

  function refresh() {
    api<{ order_bumps: any[] }>("/api/admin/order-bumps").then((r) => setItems(r.order_bumps));
  }
  useEffect(refresh, []);

  function resetForm() {
    setForm(EMPTY_BUMP_FORM);
    setEditingId(null);
    setError(null);
  }

  function startEdit(b: any) {
    setEditingId(b.id);
    setError(null);
    setForm({
      key: b.key,
      internal_name: b.internalName,
      headline: b.headline,
      description: b.description,
      checkbox_label: b.checkboxLabel,
      price_monthly_cents: b.priceMonthlyCents,
      compare_at_monthly_cents: b.compareAtMonthlyCents ?? "",
      image_url: b.imageUrl ?? "",
      video_url: b.videoUrl ?? "",
      sort_order: b.sortOrder,
    });
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const body = { ...form, compare_at_monthly_cents: form.compare_at_monthly_cents || null };
    try {
      if (editingId) {
        const { key: _key, ...fields } = body;
        await api(`/api/admin/order-bumps/${editingId}`, { method: "PATCH", body: JSON.stringify(fields) });
      } else {
        // New bumps start off; turn them on once the n8n unlock step is live.
        await api("/api/admin/order-bumps", { method: "POST", body: JSON.stringify({ ...body, active: false }) });
      }
      resetForm();
      refresh();
    } catch (err) {
      setError(String(err));
    }
  }

  async function setActive(id: string, active: boolean) {
    setError(null);
    try {
      await api(`/api/admin/order-bumps/${id}`, { method: "PATCH", body: JSON.stringify({ active }) });
      refresh();
    } catch (err) {
      setError(String(err));
    }
  }

  return (
    <div className="space-y-6">
      <h2 className="font-semibold">Order bumps</h2>
      <p className="text-xs text-ink-2">
        A key is the zenpost bonus it unlocks and cannot change. Prices apply to new sales only: existing
        subscribers keep the price they bought at.
      </p>
      <form onSubmit={save} className="card-glass p-4 grid grid-cols-2 gap-2 text-sm">
        {editingId ? (
          <input className="input font-mono" value={form.key} disabled />
        ) : (
          <select
            className="input"
            value={form.key}
            onChange={(e) => setForm({ ...form, key: e.target.value })}
            required
          >
            <option value="">Bônus (AppFeature key)…</option>
            {freeKeys.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        )}
        <input
          className="input"
          placeholder="Nome interno"
          value={form.internal_name}
          onChange={(e) => setForm({ ...form, internal_name: e.target.value })}
          required
        />
        <input
          className="input col-span-2"
          placeholder="Headline"
          value={form.headline}
          onChange={(e) => setForm({ ...form, headline: e.target.value })}
          required
        />
        <textarea
          className="input col-span-2"
          placeholder="Descrição curta"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          required
        />
        <input
          className="input col-span-2"
          placeholder="Texto do checkbox"
          value={form.checkbox_label}
          onChange={(e) => setForm({ ...form, checkbox_label: e.target.value })}
          required
        />
        <input
          className="input col-span-2"
          type="url"
          placeholder="URL da imagem (opcional)"
          value={form.image_url}
          onChange={(e) => setForm({ ...form, image_url: e.target.value })}
        />
        <input
          className="input col-span-2"
          type="url"
          placeholder="URL do vídeo (opcional)"
          value={form.video_url}
          onChange={(e) => setForm({ ...form, video_url: e.target.value })}
        />
        <input
          className="input"
          type="number"
          min={1}
          required
          placeholder="Centavos/mês"
          value={form.price_monthly_cents || ""}
          onChange={(e) => setForm({ ...form, price_monthly_cents: +e.target.value })}
        />
        <input
          className="input"
          type="number"
          min={1}
          placeholder='Centavos/mês do "de" (opcional)'
          title="Preço riscado no checkout, só exibição. Vazio: 30% acima do preço."
          value={form.compare_at_monthly_cents}
          onChange={(e) =>
            setForm({ ...form, compare_at_monthly_cents: e.target.value === "" ? "" : +e.target.value })
          }
        />
        <input
          className="input"
          type="number"
          placeholder="Ordem"
          value={form.sort_order}
          onChange={(e) => setForm({ ...form, sort_order: +e.target.value })}
        />
        <button className="btn-primary">{editingId ? "Salvar alterações" : "Criar order bump"}</button>
        {editingId ? (
          <button type="button" className="underline" onClick={resetForm}>
            Cancelar edição
          </button>
        ) : null}
        {error ? <p className="col-span-2 text-danger text-xs">{error}</p> : null}
      </form>
      <ul className="text-sm border rounded divide-y">
        {items.map((b) => (
          <li key={b.id} className="p-2 flex justify-between gap-4">
            <span>
              <span className="font-mono text-xs">{b.key}</span> · {b.internalName} ·{" "}
              {/* The checkout strikes the reference only when it is above the price. */}
              {b.compareAtMonthlyCents > b.priceMonthlyCents ? `de ${formatBRL(b.compareAtMonthlyCents)} por ` : ""}
              {formatBRL(b.priceMonthlyCents)}/mês · {b.active ? "ativo" : "inativo"}
            </span>
            <span className="flex gap-3 shrink-0">
              <button className="underline" onClick={() => startEdit(b)}>
                Editar
              </button>
              <button className="underline" onClick={() => setActive(b.id, !b.active)}>
                {b.active ? "Desativar" : "Ativar"}
              </button>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Coupons() {
  const [items, setItems] = useState<any[]>([]);
  const [form, setForm] = useState({ code: "", kind: "percentage", percentage: 10, amount_cents: 0 });
  function refresh() {
    api<{ coupons: any[] }>("/api/admin/coupons").then((r) => setItems(r.coupons));
  }
  useEffect(refresh, []);
  async function create(e: React.FormEvent) {
    e.preventDefault();
    await api("/api/admin/coupons", { method: "POST", body: JSON.stringify(form) });
    refresh();
  }
  return (
    <div className="space-y-6">
      <form onSubmit={create} className="card-glass p-4 grid grid-cols-2 gap-2">
        <input className="input" placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
        <select className="input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as any })}>
          <option value="percentage">percentage</option>
          <option value="fixed">fixed</option>
        </select>
        {form.kind === "percentage" ? (
          <input
            className="input"
            type="number"
            placeholder="0-100"
            value={form.percentage || ""}
            onChange={(e) => setForm({ ...form, percentage: +e.target.value })}
          />
        ) : (
          <div>
            <input
              className="input w-full"
              type="number"
              min={0}
              placeholder="valor em centavos (ex: 2500)"
              value={form.amount_cents || ""}
              onChange={(e) => setForm({ ...form, amount_cents: +e.target.value })}
            />
            <p className="text-xs text-ink-2 mt-1">
              = <span className="font-medium">{formatBRL(form.amount_cents || 0)}</span> · valor em
              centavos (2500 = R$ 25,00)
            </p>
          </div>
        )}
        <button className="btn-primary col-span-2">Criar cupom</button>
      </form>

      <ul className="text-sm border rounded divide-y">
        {items.map((c) => (
          <li key={c.id} className="p-2 flex justify-between">
            <span>
              {c.code} · {c.kind === "fixed" ? formatBRL(c.amountCents ?? 0) : `${c.percentage ?? 0}%`}
            </span>
            <button
              className="underline"
              onClick={async () => {
                await api(`/api/admin/coupons/${c.id}`, { method: "DELETE" });
                refresh();
              }}
            >
              Delete
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function JobsView() {
  const [items, setItems] = useState<any[]>([]);
  function refresh() {
    api<{ jobs: any[] }>("/api/admin/jobs/failed").then((r) => setItems(r.jobs));
  }
  useEffect(refresh, []);
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <h2 className="font-semibold">Failed jobs</h2>
        <button
          className="btn-ghost"
          onClick={async () => {
            await api("/api/admin/jobs/run-due", { method: "POST" });
            refresh();
          }}
        >
          Drenar fila agora
        </button>
      </div>
      <ul className="text-sm border rounded divide-y">
        {items.map((j) => (
          <li key={j.id} className="p-2 flex justify-between">
            <div>
              <div>{j.kind}</div>
              <div className="text-xs text-ink-2">{j.lastError}</div>
            </div>
            <button
              className="underline"
              onClick={async () => {
                await api(`/api/admin/jobs/${j.id}/retry`, { method: "POST" });
                refresh();
              }}
            >
              Retry
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Webhooks() {
  const [items, setItems] = useState<any[]>([]);
  useEffect(() => {
    api<{ events: any[] }>("/api/admin/webhook-events").then((r) => setItems(r.events));
  }, []);
  return (
    <ul className="text-sm border rounded divide-y">
      {items.map((e) => (
        <li key={e.id} className="p-2">
          <div className="flex justify-between">
            <span>
              {e.provider} · {e.eventType}
              {e.signatureValid === false ? " · ⚠ bad signature" : ""}
            </span>
            <span>{new Date(e.receivedAt).toLocaleString()}</span>
          </div>
          <div className="text-xs text-ink-2 truncate">{e.providerEventId}</div>
          <details className="mt-1">
            <summary className="cursor-pointer text-xs text-ink-2">payload</summary>
            <pre className="text-xs bg-black/5 rounded p-2 overflow-auto max-h-64">{JSON.stringify(e.rawPayload, null, 2)}</pre>
          </details>
        </li>
      ))}
    </ul>
  );
}
